import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertCircle } from 'lucide-react';

import { useApiData } from '../hooks/useApiData.js';
import { ErrorBanner, InlineSpinner } from '../components/common/PageState.jsx';
import {
  analyticsAgents,
  analyticsOverview,
  analyticsPipeline,
  analyticsResponseTime,
  analyticsSources,
} from '../services/api.js';
import {
  leads as allLeads,
  agents,
  activities,
  showings,
  NOW_ISO,
} from '../data/seedData.js';

const NOW = new Date(NOW_ISO);
const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
const TZ_OFFSET_HOURS = -4;
const SLOW_RESPONSE_MIN = 5;

const CLOSED_STATUSES = new Set(['closed_won', 'closed_lost', 'disqualified', 'dead']);

// ── date range ─────────────────────────────────────────────────────────────

const RANGES = [
  { value: 'week', label: 'This Week', days: 7 },
  { value: 'month', label: 'This Month', days: 30 },
  { value: 'quarter', label: 'This Quarter', days: 90 },
  { value: 'all', label: 'All Time', days: null },
];

function rangeStart(rangeValue) {
  const r = RANGES.find((x) => x.value === rangeValue);
  if (!r || r.days === null) return null;
  return new Date(NOW.getTime() - r.days * DAY_MS);
}

function inRange(iso, start) {
  if (!start) return true;
  if (!iso) return false;
  return new Date(iso) >= start;
}

function localDateKey(date) {
  const shifted = new Date(date.getTime() + TZ_OFFSET_HOURS * HOUR_MS);
  return shifted.toISOString().slice(0, 10);
}

function shortDateLabel(key) {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// ── source labels / colors ─────────────────────────────────────────────────

const SOURCE_META = {
  zillow: { label: 'Zillow', color: '#F87171' },
  facebook_ads: { label: 'Facebook', color: '#A78BFA' },
  instagram: { label: 'Instagram', color: '#F472B6' },
  whatsapp: { label: 'WhatsApp', color: '#14B8A6' },
  website: { label: 'Website', color: '#60A5FA' },
  referral: { label: 'Referral', color: '#34D399' },
  manual: { label: 'Manual', color: '#9CA3AF' },
};

function sourceMeta(src) {
  return SOURCE_META[src] || { label: src, color: '#94a3b8' };
}

// ── helpers ────────────────────────────────────────────────────────────────

function formatResponseTime(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  const totalSeconds = Math.round(minutes * 60);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

// Compact duration label for chart axes / tooltips: hours when >1h, minutes
// when >1m, otherwise seconds. Keeps the Y-axis to 3-4 characters even when
// a long-tail outlier pushes the scale into the tens of thousands of
// seconds — otherwise a raw "350000s" would blow past any reasonable label
// width.
function formatDurationShort(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  if (seconds >= 3600) {
    const hours = seconds / 3600;
    return hours >= 10 || Number.isInteger(hours)
      ? `${Math.round(hours)}h`
      : `${hours.toFixed(1)}h`;
  }
  if (seconds >= 60) {
    return `${Math.round(seconds / 60)}m`;
  }
  return `${Math.round(seconds)}s`;
}

// Response time in minutes, keyed by lead id. Computed once.
function computeResponseByLead() {
  const map = new Map();
  for (const lead of allLeads) {
    const firstMs = new Date(lead.firstContactAt).getTime();
    const agentMs = activities
      .filter(
        (a) =>
          a.leadId === lead.id && (a.type === 'agent_call' || a.type === 'agent_note'),
      )
      .map((a) => new Date(a.createdAt).getTime())
      .sort((a, b) => a - b)[0];
    if (!agentMs) continue;
    map.set(lead.id, Math.max(0, (agentMs - firstMs) / 60_000));
  }
  return map;
}

const RESPONSE_BY_LEAD = computeResponseByLead();

// ── chart theming ──────────────────────────────────────────────────────────

const TOOLTIP_STYLE = {
  backgroundColor: '#FFFFFF',
  border: '1px solid #E8EAEF',
  borderRadius: 4,
  color: '#333333',
  fontSize: 12,
  padding: '8px 12px',
  boxShadow: '0 6px 20px 0 rgba(0,0,0,0.08)',
};
const TOOLTIP_LABEL_STYLE = { color: '#7C7F95', marginBottom: 4 };
const TOOLTIP_ITEM_STYLE = { color: '#333333' };
const AXIS_TICK = { fill: '#7C7F95', fontSize: 12 };
const GRID_COLOR = '#E8EAEF';
const AXIS_COLOR = '#E8EAEF';
const LABEL_COLOR = '#333333';

// ── subcomponents ──────────────────────────────────────────────────────────

function RangeTabs({ value, onChange }) {
  return (
    <div
      role="tablist"
      aria-label="Date range"
      className="inline-flex items-center gap-1 p-1 rounded-pill bg-white border border-border-card shadow-card"
    >
      {RANGES.map((r) => {
        const active = r.value === value;
        return (
          <button
            key={r.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(r.value)}
            className={`px-3.5 py-1.5 rounded-pill text-xs md:text-sm font-semibold transition-colors ${
              active
                ? 'bg-accent text-white'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-subtle'
            }`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = '' }) {
  return (
    <section className={`card p-6 md:px-7 md:py-6 space-y-4 ${className}`}>
      <header>
        <h2 className="section-header mb-1">{title}</h2>
        {subtitle && (
          <p className="text-xs md:text-sm text-text-secondary">{subtitle}</p>
        )}
      </header>
      {children}
    </section>
  );
}

// ── response time trend ───────────────────────────────────────────────────

function ResponseTimeTrendChart({ start }) {
  const data = useMemo(() => {
    const buckets = new Map();
    for (const lead of allLeads) {
      if (!inRange(lead.firstContactAt, start)) continue;
      const minutes = RESPONSE_BY_LEAD.get(lead.id);
      if (!Number.isFinite(minutes)) continue;
      const key = localDateKey(new Date(lead.firstContactAt));
      const list = buckets.get(key) || [];
      list.push(minutes);
      buckets.set(key, list);
    }
    return Array.from(buckets.entries())
      .map(([key, vals]) => ({
        dateKey: key,
        date: shortDateLabel(key),
        seconds: Math.round((vals.reduce((s, m) => s + m, 0) / vals.length) * 60),
      }))
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  }, [start]);

  if (data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-sm text-text-secondary">
        No response activity in this range.
      </div>
    );
  }

  return (
    <div className="h-64 md:h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: 8 }}>
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            stroke={AXIS_COLOR}
            tick={AXIS_TICK}
            tickLine={false}
          />
          <YAxis
            stroke={AXIS_COLOR}
            tick={AXIS_TICK}
            tickLine={false}
            tickFormatter={formatDurationShort}
            width={72}
          />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
            itemStyle={TOOLTIP_ITEM_STYLE}
            formatter={(v) => [formatDurationShort(v), 'Avg response']}
            cursor={{ stroke: '#EA580C', strokeOpacity: 0.25 }}
          />
          <Line
            type="monotone"
            dataKey="seconds"
            stroke="#EA580C"
            strokeWidth={2}
            dot={{ r: 3, fill: '#EA580C', stroke: '#FFFFFF', strokeWidth: 2 }}
            activeDot={{ r: 5, fill: '#EA580C' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── lead source breakdown ─────────────────────────────────────────────────

function SourceBreakdownChart({ leads }) {
  const data = useMemo(() => {
    const counts = new Map();
    for (const lead of leads) {
      counts.set(lead.source, (counts.get(lead.source) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([source, count]) => ({
        source,
        label: sourceMeta(source).label,
        color: sourceMeta(source).color,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [leads]);

  if (data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-sm text-text-secondary">
        No leads in this range.
      </div>
    );
  }

  const height = Math.max(220, data.length * 44);

  return (
    <div className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 40, bottom: 5, left: 10 }}
        >
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            stroke={AXIS_COLOR}
            tick={AXIS_TICK}
            tickLine={false}
            allowDecimals={false}
          />
          <YAxis
            dataKey="label"
            type="category"
            stroke={AXIS_COLOR}
            tick={AXIS_TICK}
            tickLine={false}
            width={90}
          />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
            itemStyle={TOOLTIP_ITEM_STYLE}
            cursor={{ fill: '#EA580C', fillOpacity: 0.08 }}
            formatter={(v) => [v, 'Leads']}
          />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {data.map((d) => (
              <Cell key={d.source} fill={d.color} />
            ))}
            <LabelList
              dataKey="count"
              position="right"
              fill={LABEL_COLOR}
              fontSize={12}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── conversion funnel ─────────────────────────────────────────────────────
// Each stage is a strict subset of the previous one so counts monotonically
// decrease. Status progression:
//   any → qualified → verified → showing → closed_won.

const QUALIFIED_STAGES = new Set(['qualified', 'verified', 'showing', 'closed_won']);
const VERIFIED_STAGES = new Set(['verified', 'showing', 'closed_won']);
const SHOWING_STAGES = new Set(['showing', 'closed_won']);

const FUNNEL_COLORS = ['#94A3B8', '#FB923C', '#FBBF24', '#34D399', '#22C55E'];

function ConversionFunnel({ leads }) {
  const totalLeads = leads.length;
  const qualified = leads.filter((l) => QUALIFIED_STAGES.has(l.status)).length;
  const verified = leads.filter((l) => VERIFIED_STAGES.has(l.status)).length;
  const showing = leads.filter((l) => SHOWING_STAGES.has(l.status)).length;
  const closedWon = leads.filter((l) => l.status === 'closed_won').length;

  const stages = [
    { label: 'Total Leads', value: totalLeads },
    { label: 'Qualified', value: qualified },
    { label: 'Verified', value: verified },
    { label: 'Showing', value: showing },
    { label: 'Closed Won', value: closedWon },
  ];

  const maxValue = Math.max(1, ...stages.map((s) => s.value));

  return (
    <div className="space-y-3">
      {stages.map((s, i) => {
        const color = FUNNEL_COLORS[i] || FUNNEL_COLORS[FUNNEL_COLORS.length - 1];
        const widthPct = (s.value / maxValue) * 100;
        const prev = i === 0 ? null : stages[i - 1].value;
        const pctOfPrev =
          prev && prev > 0 ? Math.round((s.value / prev) * 100) : null;
        return (
          <div key={s.label} className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-text-primary">{s.label}</span>
              <span className="tabular-nums text-text-secondary">
                {s.value}
                {pctOfPrev !== null && (
                  <span className="ml-2 text-text-secondary">
                    {pctOfPrev}% of prev
                  </span>
                )}
              </span>
            </div>
            <div className="h-8 rounded-btn bg-bg-subtle border border-border-card overflow-hidden">
              <div
                className="h-full flex items-center pl-3 text-xs font-semibold text-white"
                style={{
                  width: `${Math.max(4, widthPct)}%`,
                  backgroundColor: color,
                  transition: 'width 300ms ease',
                }}
              >
                {s.value > 0 && widthPct > 12 ? s.value : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── score distribution ────────────────────────────────────────────────────

const SCORE_BUCKETS = [
  { label: '0-19', min: 0, max: 19, color: '#F87171' },
  { label: '20-39', min: 20, max: 39, color: '#F87171' },
  { label: '40-59', min: 40, max: 59, color: '#FBBF24' },
  { label: '60-79', min: 60, max: 79, color: '#FBBF24' },
  { label: '80-100', min: 80, max: 100, color: '#34D399' },
];

function ScoreDistributionChart({ leads }) {
  const data = useMemo(() => {
    return SCORE_BUCKETS.map((b) => ({
      label: b.label,
      color: b.color,
      count: leads.filter((l) => l.score >= b.min && l.score <= b.max).length,
    }));
  }, [leads]);

  const empty = data.every((d) => d.count === 0);

  return (
    <div className="h-64 md:h-72 w-full">
      {empty ? (
        <div className="h-full flex items-center justify-center text-sm text-text-secondary">
          No leads in this range.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              stroke={AXIS_COLOR}
              tick={AXIS_TICK}
              tickLine={false}
            />
            <YAxis
              stroke={AXIS_COLOR}
              tick={AXIS_TICK}
              tickLine={false}
              allowDecimals={false}
              width={40}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
              cursor={{ fill: '#EA580C', fillOpacity: 0.08 }}
              formatter={(v) => [v, 'Leads']}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {data.map((d) => (
                <Cell key={d.label} fill={d.color} />
              ))}
              <LabelList dataKey="count" position="top" fill={LABEL_COLOR} fontSize={12} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── agent performance table ───────────────────────────────────────────────

const ROLE_LABEL = {
  team_leader: 'Team Leader',
  agent: 'Agent',
  isa: 'ISA',
};

function AgentPerformanceTable({ leads, start }) {
  const rows = useMemo(() => {
    return agents
      .map((agent) => {
        const assigned = leads.filter((l) => l.assignedAgentId === agent.id);
        const active = assigned.filter((l) => !CLOSED_STATUSES.has(l.status)).length;
        const avgResponseMinutes = agent.stats?.avgResponseMinutes ?? null;
        const showingsCompleted = showings.filter(
          (s) =>
            s.agentId === agent.id &&
            s.status === 'completed' &&
            inRange(s.scheduledAt, start),
        ).length;
        const dealsClosed =
          agent.role === 'isa'
            ? agent.stats?.showingsBookedMTD ?? 0
            : agent.stats?.closingsYTD ?? 0;

        let conversionRate = 0;
        if (agent.role === 'isa') {
          const q = agent.stats?.leadsQualifiedMTD ?? 0;
          const b = agent.stats?.showingsBookedMTD ?? 0;
          conversionRate = q > 0 ? (b / q) * 100 : 0;
        } else {
          const closings = agent.stats?.closingsYTD ?? 0;
          const denom = closings + active;
          conversionRate = denom > 0 ? (closings / denom) * 100 : 0;
        }

        return {
          agent,
          active,
          avgResponseMinutes,
          conversionRate,
          showingsCompleted,
          dealsClosed,
        };
      })
      .sort((a, b) => b.conversionRate - a.conversionRate);
  }, [leads, start]);

  const topId = rows[0]?.agent.id;

  return (
    <div className="card p-0 overflow-hidden w-full">
      <div className="px-6 md:px-7 pt-6 md:pt-7 pb-3">
        <h2 className="section-header mb-1">Agent Performance</h2>
        <p className="text-xs md:text-sm text-text-secondary">
          Sorted by conversion rate.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" style={{ fontSize: '15px' }}>
          <thead className="bg-bg-subtle border-y border-border-card">
            <tr>
              <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                Agent
              </th>
              <th style={{ fontSize: '14px' }} className="px-4 py-3 text-right font-medium uppercase tracking-wider text-text-secondary">
                Active Leads
              </th>
              <th style={{ fontSize: '14px' }} className="px-4 py-3 text-right font-medium uppercase tracking-wider text-text-secondary">
                Avg Response
              </th>
              <th style={{ fontSize: '14px' }} className="px-4 py-3 text-right font-medium uppercase tracking-wider text-text-secondary">
                Conversion
              </th>
              <th style={{ fontSize: '14px' }} className="px-4 py-3 text-right font-medium uppercase tracking-wider text-text-secondary">
                Showings Completed
              </th>
              <th style={{ fontSize: '14px' }} className="px-4 py-3 text-right font-medium uppercase tracking-wider text-text-secondary">
                Deals Closed
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(
              ({
                agent,
                active,
                avgResponseMinutes,
                conversionRate,
                showingsCompleted,
                dealsClosed,
              }) => {
                const slow =
                  Number.isFinite(avgResponseMinutes) &&
                  avgResponseMinutes > SLOW_RESPONSE_MIN;
                const isTop = agent.id === topId;
                return (
                  <tr
                    key={agent.id}
                    className={`border-t border-border-card hover:bg-bg-subtle transition-colors ${
                      isTop ? 'bg-[#FAFAFA]' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
                          style={{ backgroundColor: agent.avatarColor || '#EA580C' }}
                        >
                          {agent.initials}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-text-primary truncate">
                            {agent.name}
                          </div>
                          <div className="text-xs text-text-secondary truncate">
                            {ROLE_LABEL[agent.role] || agent.role}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-text-primary">
                      {active}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span
                        className={`inline-flex items-center gap-1 justify-end ${
                          slow ? 'text-[#F87171]' : 'text-text-primary'
                        }`}
                      >
                        {slow && <AlertCircle size={13} />}
                        {formatResponseTime(avgResponseMinutes)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-text-primary">
                      {conversionRate.toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-text-primary">
                      {showingsCompleted}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-text-primary">
                      {dealsClosed}
                    </td>
                  </tr>
                );
              },
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── page ───────────────────────────────────────────────────────────────────

export default function Analytics() {
  const [range, setRange] = useState('month');
  const start = useMemo(() => rangeStart(range), [range]);

  // Fire all analytics endpoints so live dashboards refresh when available.
  // Charts still render from seed data when the backend returns sparse
  // analytics payloads — we only surface loading / error at the header.
  const overview = useApiData(analyticsOverview, []);
  const pipeline = useApiData(analyticsPipeline, []);
  const sources = useApiData(analyticsSources, []);
  const agentsReq = useApiData(analyticsAgents, []);
  const responseTime = useApiData(analyticsResponseTime, []);

  const anyLoading =
    overview.loading ||
    pipeline.loading ||
    sources.loading ||
    agentsReq.loading ||
    responseTime.loading;

  const firstError =
    overview.error ||
    pipeline.error ||
    sources.error ||
    agentsReq.error ||
    responseTime.error;

  const refetchAll = () => {
    overview.refetch();
    pipeline.refetch();
    sources.refetch();
    agentsReq.refetch();
    responseTime.refetch();
  };

  const filteredLeads = useMemo(
    () => allLeads.filter((l) => inRange(l.createdAt, start)),
    [start],
  );

  const activeCount = filteredLeads.filter((l) => !CLOSED_STATUSES.has(l.status)).length;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="page-title mb-0">Analytics</h1>
            {anyLoading && <InlineSpinner />}
          </div>
          <p className="page-subtitle mb-0 mt-2">
            {filteredLeads.length} leads in range · {activeCount} active
          </p>
        </div>
        <RangeTabs value={range} onChange={setRange} />
      </header>

      {firstError && (
        <ErrorBanner
          message="Couldn’t refresh analytics from the server."
          onRetry={refetchAll}
        />
      )}

      <div className="grid grid-cols-1 lg:[grid-template-columns:repeat(2,minmax(0,1fr))] gap-6">
        <ChartCard
          title="Response Time Trend"
          subtitle="Average time from first contact to first agent response."
        >
          <ResponseTimeTrendChart start={start} />
        </ChartCard>

        <ChartCard
          title="Lead Source Breakdown"
          subtitle="Where new leads came from."
        >
          <SourceBreakdownChart leads={filteredLeads} />
        </ChartCard>

        <ChartCard
          title="Conversion Funnel"
          subtitle="Drop-off from inbox to closed deal."
        >
          <ConversionFunnel leads={filteredLeads} />
        </ChartCard>

        <ChartCard
          title="Score Distribution"
          subtitle="How leads cluster by qualification score."
        >
          <ScoreDistributionChart leads={filteredLeads} />
        </ChartCard>
      </div>

      <AgentPerformanceTable leads={filteredLeads} start={start} />
    </div>
  );
}
